import { Injectable, inject } from '@angular/core';
import { CreateTimeSeriesDataPointDto } from '@anessa/shared-models';
import { CsvSeparator } from '../../../shared/services/file-export/file-export.service';
import { FileImportService } from '../../../shared/services/file-import/file-import.service';
import { Utils } from '../../../shared/utils';
import { TimeSeriesTable } from '../models/timeseries-table.model';
import { TimeSeriesValidationService } from './timeseries-validation.service';

export interface TimeSeriesImportError {
  row: number;
  messageKey: string;
  messageParams?: Record<string, string | number>;
}

export interface TimeSeriesParseResult {
  points: CreateTimeSeriesDataPointDto[];
  unknownHeaders: string[];
  errors: TimeSeriesImportError[];
}

@Injectable({ providedIn: 'root' })
export class TimeSeriesImportService {
  private readonly fileImport = inject(FileImportService);
  private readonly validationService = inject(TimeSeriesValidationService);

  async parseFile(
    file: File,
    table: TimeSeriesTable,
    separator: CsvSeparator = ',',
    opts: { sheetIndex?: number } = {},
  ): Promise<TimeSeriesParseResult> {
    const rows = await this.fileImport.readSheet(file, separator, { sheetIndex: opts.sheetIndex });

    if (rows.length < 2) {
      return {
        points: [],
        unknownHeaders: [],
        errors: [{ row: 1, messageKey: 'error_file_empty' }],
      };
    }

    const fileHeaders: string[] = rows[0].map((h) => String(h ?? '').trim());

    // Match timestamp column
    const timestampIdx = fileHeaders.findIndex(
      (h) => h.toLowerCase() === table.timestampField.toLowerCase(),
    );

    // Build column-to-index map using importColumnName || label
    const columnMap: { key: string; idx: number }[] = [];
    const knownHeaders = new Set<string>();
    if (timestampIdx >= 0)
      knownHeaders.add(fileHeaders[timestampIdx].toLowerCase());

    for (const column of table.columns) {
      const headerName = (
        column.importColumnName || column.label
      ).toLowerCase();
      const idx = fileHeaders.findIndex((h) => h.toLowerCase() === headerName);
      if (idx >= 0) {
        columnMap.push({ key: column.key, idx });
        knownHeaders.add(fileHeaders[idx].toLowerCase());
      }
    }

    const unknownHeaders = fileHeaders.filter(
      (h) => h.trim() && !knownHeaders.has(h.toLowerCase()),
    );

    const points: CreateTimeSeriesDataPointDto[] = [];
    const errors: TimeSeriesImportError[] = [];

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];

      // Skip fully empty rows
      if (row.every((cell) => cell === '' || cell == null)) continue;

      const timestampCell = timestampIdx >= 0 ? row[timestampIdx] : '';
      const timestamp = String(timestampCell ?? '').trim();
      if (!timestamp) {
        // Row 2 (r === 1, immediately after the header) with an empty timestamp
        // but data in other cells is treated as a unit row (see export "Include
        // unit row" option) — skip it silently. Any other row missing a
        // timestamp is a real error.
        const isUnitRow =
          r === 1 &&
          row.some(
            (cell, i) => i !== timestampIdx && cell !== '' && cell != null,
          );
        if (!isUnitRow) {
          errors.push({
            row: r + 1,
            messageKey: 'error_missing_required_timestamp',
            messageParams: {
              column: table.timestampField,
            },
          });
        }
        continue;
      }

      const values: Record<string, string> = {};
      for (const { key, idx } of columnMap) {
        const val = row[idx];
        if (val !== '' && val != null) {
          values[key] = String(val);
        }
      }

      const parsedTimestamp = Utils.parseUtcDateTime(timestampCell ?? '');
      const normalizedTimestamp = parsedTimestamp
        ? parsedTimestamp.toISOString()
        : timestamp;

      points.push({
        timestamp: normalizedTimestamp,
        values,
        status: this.validationService.inferStatusFromValues(
          table.columns,
          values,
        ),
      });
    }

    return { points, unknownHeaders, errors };
  }
}
